import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Container,
  Typography,
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Chip,
  Fab,
  Tooltip,
  Snackbar,
  CircularProgress,
  Card,
  CardContent,
  Grid,
  Avatar,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  DialogContentText,
  Switch,
  FormControlLabel,
  Tabs,
  Tab,
  InputAdornment,
  Stack,
  Divider,
  LinearProgress,
  alpha,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Settings as SettingsIcon,
  SmartToy as BotIcon,
  Business as BusinessIcon,
  Psychology as PsychologyIcon,
  WhatsApp as WhatsAppIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  VpnKey as VpnKeyIcon,
  Storefront as StorefrontIcon,
  Sync as SyncIcon,
  Lock as LockIcon,
  LockOpen as LockOpenIcon,
  AttachMoney as AttachMoneyIcon,
  Refresh as RefreshIcon,
  ErrorOutline as ErrorOutlineIcon,
  CheckCircleOutline as CheckCircleOutlineIcon,
  Inbox as InboxIcon,
  Forum as ForumIcon,
  Groups as GroupsIcon,
  Schedule as ScheduleIcon,
  Cached as CachedIcon,
  Send as SendIcon,
  OpenInNew as OpenInNewIcon,
  FilterList as FilterListIcon,
  Description as DescriptionIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_URL } from '../config';
import whatsappWebService, {
  WhatsAppWebNumber,
  whatsappCloudService,
  WhatsAppCloudNumberStatusResponse,
  WhatsAppInboundEventResult,
  WhatsAppInboundEventSource,
  WhatsAppCloudSendResponse,
  WhatsAppConversation,
  WhatsAppInboundEventLogSummary,
  WhatsAppInboundEventLogDetail,
  ListCloudInboundEventsParams,
} from '../services/whatsappWebService';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function CustomTabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div role="tabpanel" hidden={value !== index} id={`chatbot-config-tabpanel-${index}`} {...other}>
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

interface ChatbotPromptConfig {
  id: number;
  business_type: string;
  main_prompt: string;
  personality: string;
  objectives: string;
  benefits?: string;
  questions?: string;
  closing_phrases?: string;
  tone: string;
  is_active: boolean;
  voice_enabled?: boolean;
  voice_type?: string;
  voice_speed?: number;
  voice_model?: string;
  auto_voice_response?: boolean;
  created_at: string;
  updated_at: string;
}

interface ChatbotPromptConfigFormData {
  business_type: string;
  main_prompt: string;
  personality: string;
  objectives: string;
  benefits: string;
  questions: string;
  closing_phrases: string;
  tone: string;
  is_active: boolean;
  voice_enabled: boolean;
  voice_type: string;
  voice_speed: number;
  voice_model: string;
  auto_voice_response: boolean;
}

interface WhatsAppCloudApiFormData {
  label: string;
  phone_number_id: string;
  access_token: string;
  business_account_id: string;
}

const EMPTY_WHATSAPP_FORM: WhatsAppCloudApiFormData = {
  label: 'WhatsApp Cloud API',
  phone_number_id: '',
  access_token: '',
  business_account_id: '',
};

const PRETTY_EVENT_RESULT: Record<WhatsAppInboundEventResult, string> = {
  signature_invalid: 'Assinatura do webhook inválida',
  app_secret_missing: 'APP_SECRET não configurado no servidor',
  verify_token_mismatch: 'Verify Token não confere',
  no_matching_phone_number_id: 'Phone Number ID não cadastrado na sua organização',
  provider_mismatch: 'Número tem provider incompatível com este canal de entrada',
  discarded_duplicate_external_message_id: 'Mensagem duplicada descartada',
  ingested: 'Mensagem recebida e gravada com sucesso',
  parse_error: 'Payload JSON inválido no webhook',
  bridge_unreachable: 'Bridge Baileys sem resposta',
  unknown_error: 'Erro não catalogado (ver log do servidor)',
};

const formatRelative = (iso: string | null | undefined) => {
  if (!iso) return { label: 'Nunca', title: '', stale: true };
  const t = new Date(iso).getTime();
  const now = Date.now();
  const diffMs = Math.max(0, now - t);
  const diffMin = Math.floor(diffMs / 60000);
  const dateLabel = new Date(iso).toLocaleString('pt-BR', {
    day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit',
  });
  if (diffMin < 1) return { label: 'agora mesmo', title: dateLabel, stale: false };
  if (diffMin < 60) return { label: `há ${diffMin} min`, title: dateLabel, stale: false };
  const diffH = Math.floor(diffMin / 60);
  if (diffH < 24) return { label: `há ${diffH} h`, title: dateLabel, stale: diffH > 2 };
  const diffD = Math.floor(diffH / 24);
  return { label: `há ${diffD} dias`, title: dateLabel, stale: true };
};

const WhatsAppCloudApiTab: React.FC<{
  showSnackbar: (message: string, severity: 'success' | 'error' | 'info' | 'warning') => void;
}> = ({ showSnackbar }) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [number, setNumber] = useState<WhatsAppWebNumber | null>(null);
  const [form, setForm] = useState<WhatsAppCloudApiFormData>(EMPTY_WHATSAPP_FORM);
  const [showToken, setShowToken] = useState(false);

  // AW-009 Painel Operacional
  const [status, setStatus] = useState<WhatsAppCloudNumberStatusResponse | null>(null);
  const [statusLoading, setStatusLoading] = useState(false);
  const [lastStatusAt, setLastStatusAt] = useState<Date | null>(null);
  const lastStatusErrorSigRef = useRef<string | null>(null);
  const navigate = useNavigate();

  // AW-010 Envio Avulso
  const [sendForm, setSendForm] = useState({ to: '', content: '' });
  const [sendLoading, setSendLoading] = useState(false);
  const [lastSendResult, setLastSendResult] = useState<WhatsAppCloudSendResponse | null>(null);

  const handlePhoneMask = (raw: string) => {
    const digits = raw.replace(/\D/g, '').slice(0, 15);
    if (digits.length <= 2) return digits;
    if (digits.length <= 7) return `(${digits.slice(0, 2)}) ${digits.slice(2)}`;
    if (digits.length <= 11) return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`;
    return `+${digits.slice(0, 2)} (${digits.slice(2, 4)}) ${digits.slice(4, 9)}-${digits.slice(9)}`;
  };

  const handleSend = async () => {
    const toDigits = sendForm.to.replace(/\D/g, '');
    if (!sendForm.content.trim() || !toDigits) {
      showSnackbar('Informe o número destino e a mensagem para enviar', 'error');
      return;
    }
    setSendLoading(true);
    setLastSendResult(null);
    try {
      const response = await whatsappCloudService.sendCloudNumberText({
        to: toDigits,
        content: sendForm.content.trim(),
        message_type: 'text',
        number_id: number?.id ?? null,
      });
      setLastSendResult(response);
      if (response.success) {
        showSnackbar(`Mensagem enviada com sucesso via ${response.provider_used || 'provider'}`, 'success');
        setSendForm((f) => ({ ...f, content: '' }));
        await Promise.all([fetchStatus(true), loadRecentConversations()]);
      } else if (response.paid_sending_was_blocked) {
        showSnackbar('Envio Pago BLOQUEADO no backend (WHATSAPP_PAID_SENDING_ENABLED=false). Habilite esta flag no .env do backend para disparar mensagens pagas pela Cloud API.', 'warning');
      } else {
        showSnackbar(response.error || response.detail || 'Erro ao enviar mensagem', 'error');
      }
    } catch (error: any) {
      showSnackbar(error?.response?.data?.detail || 'Erro ao enviar mensagem', 'error');
    }
    setSendLoading(false);
  };

  // AW-011 Inbox + Últimas 5 conversas
  const [recentConversations, setRecentConversations] = useState<WhatsAppConversation[]>([]);
  const [conversationsLoading, setConversationsLoading] = useState(false);
  const loadRecentConversations = useCallback(async () => {
    if (!number?.id) { setRecentConversations([]); return; }
    setConversationsLoading(true);
    try {
      const list = await whatsappWebService.listConversations({ sender_number_id: number.id, limit: 5 });
      setRecentConversations(list);
    } catch { /* ignore */ }
    setConversationsLoading(false);
  }, [number?.id]);

  // AW-012 Tabela eventos inbound
  const [inboundPagination, setInboundPagination] = useState({ page: 1, per_page: 10, total: 0 });
  const [inboundList, setInboundList] = useState<WhatsAppInboundEventLogSummary[]>([]);
  const [inboundLoading, setInboundLoading] = useState(false);
  const [inboundFilters, setInboundFilters] = useState<{
    event_result: WhatsAppInboundEventResult[];
    source: WhatsAppInboundEventSource | '';
    date_from: string;
    date_to: string;
    remote_phone: string;
  }>({ event_result: [], source: '', date_from: '', date_to: '', remote_phone: '' });
  const [detailOpen, setDetailOpen] = useState(false);
  const [selectedEventDetail, setSelectedEventDetail] = useState<WhatsAppInboundEventLogDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [inboundFiltersOpen, setInboundFiltersOpen] = useState(false);

  const loadInboundList = useCallback(async () => {
    setInboundLoading(true);
    try {
      const params: ListCloudInboundEventsParams = {
        page: inboundPagination.page,
        per_page: inboundPagination.per_page,
        number_id: number?.id ?? null,
        event_result: inboundFilters.event_result.length ? inboundFilters.event_result : null,
        source: inboundFilters.source || null,
        date_from: inboundFilters.date_from || null,
        date_to: inboundFilters.date_to || null,
        remote_phone: inboundFilters.remote_phone.trim() || null,
      };
      const data = await whatsappCloudService.listCloudInboundEvents(params);
      setInboundList(data.items);
      setInboundPagination((p) => ({ ...p, total: data.total, page: data.page, per_page: data.per_page }));
    } catch { /* ignore */ }
    setInboundLoading(false);
  }, [number?.id, inboundPagination.page, inboundPagination.per_page, inboundFilters]);

  const openEventDetail = async (eventId: number) => {
    setDetailLoading(true);
    setDetailOpen(true);
    setSelectedEventDetail(null);
    try {
      const data = await whatsappCloudService.getCloudInboundEventDetail(eventId);
      setSelectedEventDetail(data);
    } catch { /* ignore */ }
    setDetailLoading(false);
  };

  const toggleResultFilter = (r: WhatsAppInboundEventResult) => {
    setInboundFilters((f) => ({
      ...f,
      event_result: f.event_result.includes(r)
        ? f.event_result.filter((x) => x !== r)
        : [...f.event_result, r],
    }));
    setInboundPagination((p) => ({ ...p, page: 1 }));
  };

  const totalPages = Math.max(1, Math.ceil(inboundPagination.total / inboundPagination.per_page));

  const fetchNumber = useCallback(async () => {
    setLoading(true);
    try {
      const numbers = await whatsappWebService.listNumbers();
      const cloudApiNumber = numbers.find((n) => n.provider === 'cloud_api') || null;
      setNumber(cloudApiNumber);
      setForm(
        cloudApiNumber
          ? {
              label: cloudApiNumber.label,
              phone_number_id: cloudApiNumber.phone_number_id || '',
              access_token: '',
              business_account_id: cloudApiNumber.business_account_id || '',
            }
          : EMPTY_WHATSAPP_FORM
      );
    } catch (error) {
      showSnackbar('Erro ao carregar configuração do WhatsApp', 'error');
    }
    setLoading(false);
  }, [showSnackbar]);

  const fetchStatus = useCallback(async (silent = false) => {
    if (!silent) setStatusLoading(true);
    try {
      const data = await whatsappCloudService.getCloudNumberStatus(number?.id ?? null);
      setStatus(data);
      setLastStatusAt(new Date());
      lastStatusErrorSigRef.current = null;
    } catch (error: any) {
      const statusCode = error?.response?.status;
      const detail = typeof error?.response?.data?.detail === 'string'
        ? error.response.data.detail
        : 'sem detalhe';
      if (statusCode === 404) {
        setStatus(null);
        // 404 silencioso, showSnackbar só uma vez
        const sig = `status-404`;
        if (lastStatusErrorSigRef.current !== sig) {
          lastStatusErrorSigRef.current = sig;
        }
      } else {
        const sig = `status-${statusCode}-${detail}`;
        if (lastStatusErrorSigRef.current !== sig) {
          lastStatusErrorSigRef.current = sig;
          showSnackbar(
            `Erro ao ler status do WhatsApp: ${detail}`,
            'warning',
          );
        }
      }
    } finally {
      setStatusLoading(false);
    }
  }, [number?.id, showSnackbar]);

  useEffect(() => {
    fetchNumber();
  }, [fetchNumber]);

  useEffect(() => {
    if (!number) {
      setStatus(null);
      return;
    }
    fetchStatus(false);
    const id = window.setInterval(() => fetchStatus(true), 30000);
    return () => window.clearInterval(id);
  }, [number, fetchStatus]);

  useEffect(() => { loadRecentConversations(); }, [loadRecentConversations]);
  useEffect(() => { loadInboundList(); }, [loadInboundList]);

  const handleSave = async () => {
    if (!form.label.trim() || !form.phone_number_id.trim()) {
      showSnackbar('Rótulo e Phone Number ID são obrigatórios', 'error');
      return;
    }
    if (!number && !form.access_token.trim()) {
      showSnackbar('Access Token é obrigatório na primeira configuração', 'error');
      return;
    }

    setSaving(true);
    try {
      const updated = number
        ? await whatsappWebService.updateNumber(number.id, {
            label: form.label.trim(),
            phone_number_id: form.phone_number_id.trim(),
            business_account_id: form.business_account_id.trim() || undefined,
            access_token: form.access_token.trim() || undefined,
          })
        : await whatsappWebService.createNumber({
            label: form.label.trim(),
            phone_number_id: form.phone_number_id.trim(),
            access_token: form.access_token.trim(),
            business_account_id: form.business_account_id.trim() || undefined,
          });
      setNumber(updated);
      setForm((prev) => ({ ...prev, access_token: '' }));
      showSnackbar('Configuração do WhatsApp salva com sucesso!', 'success');
      // Refetch status imediatamente após salvar
      await fetchStatus(false);
    } catch (error: any) {
      const detail = error.response?.data?.detail;
      showSnackbar(typeof detail === 'string' ? detail : 'Erro ao salvar configuração do WhatsApp', 'error');
    }
    setSaving(false);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Stack spacing={3}>
      {/* Cabeçalho do Painel Operacional */}
      <Card variant="outlined">
        <CardContent sx={{ px: 3, py: 2 }}>
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 2,
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Stack direction="row" spacing={1.5} alignItems="center">
              <WhatsAppIcon color="primary" sx={{ fontSize: 28 }} />
              <Box>
                <Typography variant="h6" sx={{ lineHeight: 1.2 }}>
                  Painel Operacional — WhatsApp Cloud API
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Monitoramento em tempo real (atualiza a cada 30s)
                </Typography>
              </Box>
            </Stack>
            <Stack direction="row" spacing={1.5} alignItems="center">
              {lastStatusAt && (
                <Chip
                  size="small"
                  variant="outlined"
                  icon={<CachedIcon sx={{ fontSize: 14 }} />}
                  label={`Atualizado ${formatRelative(lastStatusAt.toISOString()).label}`}
                  title={lastStatusAt.toLocaleString('pt-BR')}
                  sx={{ borderRadius: 999 }}
                />
              )}
              <Button
                size="small"
                startIcon={
                  statusLoading ? <CircularProgress size={14} /> : <RefreshIcon sx={{ fontSize: 16 }} />
                }
                onClick={() => fetchStatus(false)}
                variant="outlined"
                disabled={statusLoading || !number}
              >
                Recarregar
              </Button>
            </Stack>
          </Box>
          {statusLoading && (
            <Box sx={{ mt: 2, mx: -3, mb: -2, width: 'auto' }}>
              <LinearProgress />
            </Box>
          )}
        </CardContent>
      </Card>

      {/* 4 Cards de Saúde (4 checks) */}
      <Grid container spacing={2.5}>
        {/* Card 1 — Credenciais */}
        <Grid item xs={12} sm={6} md={3}>
          <Card variant="outlined">
            <CardContent sx={{ p: 2.5 }}>
              <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
                <Box
                  sx={{
                    p: 1.25,
                    borderRadius: 2,
                    bgcolor: (t) =>
                      status?.health_checks.credentials_ok
                        ? t.palette.success.main + '1A'
                        : t.palette.error.main + '1A',
                    color: (t) =>
                      status?.health_checks.credentials_ok
                        ? t.palette.success.main
                        : t.palette.error.main,
                  }}
                >
                  <VpnKeyIcon />
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                    1. Credenciais
                  </Typography>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    {status?.health_checks.credentials_ok ? 'OK' : number ? 'Incompletas' : 'Sem número'}
                  </Typography>
                </Box>
              </Stack>
              <Chip
                size="small"
                color={status?.health_checks.credentials_ok ? 'success' : 'default'}
                variant={status?.health_checks.credentials_ok ? 'filled' : 'outlined'}
                label={
                  status?.health_checks.credentials_ok
                    ? '3 campos preenchidos'
                    : number?.has_access_token
                      ? 'Phone/BA/WABA ausentes'
                      : 'Configure abaixo'
                }
                sx={{ mb: 1.5, borderRadius: 999 }}
                icon={
                  status?.health_checks.credentials_ok ? (
                    <CheckCircleOutlineIcon sx={{ fontSize: 14 }} />
                  ) : (
                    <ErrorOutlineIcon sx={{ fontSize: 14 }} />
                  )
                }
              />
              <Stack direction="column" spacing={0.4} sx={{ fontSize: 12 }}>
                <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.4 }}>
                  <Box component="span" sx={{ fontWeight: 500 }}>Phone ID:</Box>{' '}
                  {number?.phone_number_id
                    ? number.phone_number_id
                    : '—'}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.4 }}>
                  <Box component="span" sx={{ fontWeight: 500 }}>WABA ID:</Box>{' '}
                  {number?.business_account_id || '—'}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.4 }}>
                  <Stack direction="row" spacing={0.5} alignItems="center" component="span">
                    <Box component="span" sx={{ fontWeight: 500 }}>Access Token:</Box>
                    {number?.has_access_token ? (
                      <Chip label="Configurado" size="small" variant="filled" sx={{ borderRadius: 999, px: 0, height: 16, fontSize: 10, bgcolor: (t) => alpha(t.palette.success.main, 0.12), color: 'success.main' }} />
                    ) : (
                      <Chip label="Não setado" color="default" size="small" variant="outlined" sx={{ borderRadius: 999, px: 0, height: 16, fontSize: 10 }} />
                    )}
                  </Stack>
                </Typography>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        {/* Card 2 — Perfil de Negócios (Meta) */}
        <Grid item xs={12} sm={6} md={3}>
          <Card variant="outlined">
            <CardContent sx={{ p: 2.5 }}>
              <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
                <Box
                  sx={{
                    p: 1.25,
                    borderRadius: 2,
                    bgcolor: (t) =>
                      status?.health_checks.business_profile_ok
                        ? t.palette.success.main + '1A'
                        : t.palette.warning.main + '1A',
                    color: (t) =>
                      status?.health_checks.business_profile_ok
                        ? t.palette.success.main
                        : t.palette.warning.main,
                  }}
                >
                  <StorefrontIcon />
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                    2. Perfil Meta
                  </Typography>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    {status?.health_checks.business_profile_ok
                      ? 'Consultado'
                      : !status?.health_checks.credentials_ok
                        ? 'Dependente'
                        : 'Erro na consulta'}
                  </Typography>
                </Box>
              </Stack>
              {status?.business_profile?.name ? (
                <Stack direction="column" spacing={0.5}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {status.business_profile.name}
                  </Typography>
                  {status.business_profile.vertical && (
                    <Chip
                      size="small"
                      variant="outlined"
                      label={status.business_profile.vertical}
                      sx={{ alignSelf: 'flex-start', borderRadius: 999 }}
                    />
                  )}
                  {status.business_profile.email && (
                    <Typography variant="caption" color="text.secondary">
                      ✉ {status.business_profile.email}
                    </Typography>
                  )}
                  {status.business_profile.websites?.length ? (
                    <Typography variant="caption" color="text.secondary" noWrap>
                      🌐 {status.business_profile.websites[0]}
                    </Typography>
                  ) : null}
                </Stack>
              ) : (
                <Stack direction="row" spacing={1} alignItems="flex-start">
                  <ErrorOutlineIcon color={status?.health_checks.credentials_ok ? 'warning' : 'disabled'} sx={{ fontSize: 16, mt: 0.25 }} />
                  <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.3 }}>
                    {status?.health_checks.credentials_ok
                      ? 'Confira se a credencial é válida e se este número tem Perfil de Negócios publicado no Meta Manager.'
                      : 'Preencha credenciais primeiro.'}
                  </Typography>
                </Stack>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Card 3 — Último Inbound recebido + último erro */}
        <Grid item xs={12} sm={6} md={3}>
          <Card variant="outlined">
            <CardContent sx={{ p: 2.5 }}>
              <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
                <Box
                  sx={{
                    p: 1.25,
                    borderRadius: 2,
                    bgcolor: (t) =>
                      status?.health_checks.last_webhook_ingested_at
                        ? t.palette.success.main + '1A'
                        : t.palette.info.main + '1A',
                    color: (t) =>
                      status?.health_checks.last_webhook_ingested_at
                        ? t.palette.success.main
                        : t.palette.info.main,
                  }}
                >
                  <SyncIcon />
                </Box>
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                    3. Último Inbound
                  </Typography>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, whiteSpace: 'nowrap', textOverflow: 'ellipsis', overflow: 'hidden' }}>
                    {formatRelative(status?.health_checks.last_webhook_ingested_at).label}
                  </Typography>
                </Box>
              </Stack>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 1.25, display: 'block' }}>
                {status?.health_checks.last_webhook_ingested_at
                  ? formatRelative(status.health_checks.last_webhook_ingested_at).title
                  : 'Nenhuma mensagem recebida ainda. Envie um teste do seu celular.'}
              </Typography>
              <Divider sx={{ mb: 1.25 }} />
              {status?.health_checks.last_inbound_error_event ? (
                <Alert
                  severity={(() => {
                    const r = status.health_checks.last_inbound_error_event.event_result;
                    if (r === 'ingested') return 'success';
                    if (['signature_invalid', 'app_secret_missing', 'verify_token_mismatch'].includes(r)) return 'error';
                    return 'warning';
                  })()}
                  sx={{ px: 1.5, py: 0.75, '& .MuiAlert-message': { p: 0 } }}
                  icon={<ScheduleIcon sx={{ fontSize: 16 }} />}
                >
                  <Stack direction="column" spacing={0.25}>
                    <Typography variant="caption" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
                      {PRETTY_EVENT_RESULT[status.health_checks.last_inbound_error_event.event_result] ??
                        status.health_checks.last_inbound_error_event.event_result}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {formatRelative(status.health_checks.last_inbound_error_event.created_at).label}
                      {status.health_checks.last_inbound_error_event.remote_phone_normalized
                        ? ` · ${status.health_checks.last_inbound_error_event.remote_phone_normalized}`
                        : ''}
                    </Typography>
                  </Stack>
                </Alert>
              ) : (
                <Chip
                  size="small"
                  icon={<CheckCircleOutlineIcon sx={{ fontSize: 14 }} />}
                  label="Sem erros recentes"
                  color="success"
                  variant="outlined"
                  sx={{ borderRadius: 999 }}
                />
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Card 4 — Envio / Flags Globais */}
        <Grid item xs={12} sm={6} md={3}>
          <Card variant="outlined">
            <CardContent sx={{ p: 2.5 }}>
              <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
                <Box
                  sx={{
                    p: 1.25,
                    borderRadius: 2,
                    bgcolor: (t) =>
                      status?.global_flags.paid_sending_enabled
                        ? t.palette.success.main + '1A'
                        : t.palette.warning.main + '1A',
                    color: (t) =>
                      status?.global_flags.paid_sending_enabled
                        ? t.palette.success.main
                        : t.palette.warning.main,
                  }}
                >
                  <AttachMoneyIcon />
                </Box>
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                    4. Envio Pago
                  </Typography>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    {status?.global_flags.paid_sending_enabled ? 'HABILITADO' : 'TRAVADO'}
                  </Typography>
                </Box>
              </Stack>
              <Chip
                size="small"
                color={status?.global_flags.paid_sending_enabled ? 'success' : 'warning'}
                variant="filled"
                icon={status?.global_flags.paid_sending_enabled ? <CheckCircleOutlineIcon sx={{ fontSize: 14 }} /> : <LockIcon sx={{ fontSize: 14 }} />}
                label={
                  status?.global_flags.paid_sending_enabled
                    ? 'Disparos pagos permitidos'
                    : 'WHATSAPP_PAID_SENDING_ENABLED=false'
                }
                sx={{ mb: 1.5, borderRadius: 999, alignSelf: 'stretch' }}
              />
              <Stack direction="column" spacing={0.4}>
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                  <Typography variant="caption" color="text.secondary">Provider</Typography>
                  <Chip
                    size="small"
                    label={status?.global_flags.send_provider || '—'}
                    variant="outlined"
                    sx={{ height: 18, borderRadius: 999, fontSize: 10, px: 0 }}
                  />
                </Stack>
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                  <Typography variant="caption" color="text.secondary">APP_SECRET</Typography>
                  {status?.global_flags.app_secret_set ? (
                    <LockIcon color="success" sx={{ fontSize: 14 }} />
                  ) : (
                    <LockOpenIcon color="warning" sx={{ fontSize: 14 }} />
                  )}
                </Stack>
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                  <Typography variant="caption" color="text.secondary">Verify Token</Typography>
                  {status?.global_flags.verify_token_set ? (
                    <LockIcon color="success" sx={{ fontSize: 14 }} />
                  ) : (
                    <LockOpenIcon color="warning" sx={{ fontSize: 14 }} />
                  )}
                </Stack>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        {/* Card 5 (full width) — Inbox counters */}
        <Grid item xs={12}>
          <Card variant="outlined">
            <CardContent sx={{ p: 2.5 }}>
              <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
                <Box
                  sx={{
                    p: 1.25,
                    borderRadius: 2,
                    bgcolor: (t) => t.palette.primary.main + '14',
                    color: (t) => t.palette.primary.main,
                  }}
                >
                  <InboxIcon />
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                    Atividade do Inbox
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
                    {status?.inbox_counters.total_conversations ?? 0} conversas
                  </Typography>
                </Box>
              </Stack>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={3}>
                  <Stack direction="row" spacing={1.5} alignItems="center">
                    <ForumIcon color="primary" sx={{ fontSize: 22 }} />
                    <Box>
                      <Typography variant="caption" color="text.secondary">Conversas totais</Typography>
                      <Typography variant="h6" sx={{ fontWeight: 700 }}>
                        {status?.inbox_counters.total_conversations ?? 0}
                      </Typography>
                    </Box>
                  </Stack>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Stack direction="row" spacing={1.5} alignItems="center">
                    <Chip
                      size="small"
                      color={status && status.inbox_counters.unread_count > 0 ? 'error' : 'default'}
                      label={status?.inbox_counters.unread_count ?? 0}
                      sx={{ width: 36, height: 36, borderRadius: 999, fontWeight: 700, fontSize: 16, '& .MuiChip-label': { p: 0 } }}
                    />
                    <Box>
                      <Typography variant="caption" color="text.secondary">Não lidas</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {status?.inbox_counters.unread_count ? `${status.inbox_counters.unread_count} conversa(s)` : 'Zerado'}
                      </Typography>
                    </Box>
                  </Stack>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Stack direction="row" spacing={1.5} alignItems="center">
                    <GroupsIcon color="warning" sx={{ fontSize: 22 }} />
                    <Box>
                      <Typography variant="caption" color="text.secondary">Aguardando atend. humano</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {status?.inbox_counters.waiting_human_count ?? 0} conversa(s)
                      </Typography>
                    </Box>
                  </Stack>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Stack direction="column" spacing={0.3}>
                    <Typography variant="caption" color="text.secondary">Última atividade</Typography>
                    <Typography variant="caption" sx={{ fontWeight: 500 }}>
                      Entrada: {formatRelative(status?.inbox_counters.last_inbound_at).label}
                    </Typography>
                    <Typography variant="caption" sx={{ fontWeight: 500 }}>
                      Saída: {formatRelative(status?.inbox_counters.last_outbound_at).label}
                    </Typography>
                  </Stack>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* AW-010: Envio Avulso */}
      <Card variant="outlined">
        <CardContent sx={{ p: 3 }}>
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 2,
              alignItems: 'center',
              justifyContent: 'space-between',
              mb: 2,
            }}
          >
            <Stack direction="row" spacing={1.5} alignItems="center">
              <SendIcon color="primary" sx={{ fontSize: 24 }} />
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
                  Enviar mensagem avulsa
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Teste rápido de envio pelo número Cloud API desta organização
                </Typography>
              </Box>
            </Stack>
          </Box>
          <Grid container spacing={2} sx={{ maxWidth: 720 }}>
            <Grid item xs={12} sm={5}>
              <TextField
                fullWidth
                label="Número destino (com DDD)"
                placeholder="Ex: 11965115307"
                value={sendForm.to}
                onChange={(e) => setSendForm({ ...sendForm, to: handlePhoneMask(e.target.value) })}
                helperText="Brasil: com DDD + 9 dígitos; Internacional: inicie com +"
                disabled={sendLoading || !number?.has_access_token}
              />
            </Grid>
            <Grid item xs={12} sm={7}>
              <TextField
                fullWidth
                label="Mensagem"
                multiline
                minRows={2}
                maxRows={4}
                value={sendForm.content}
                onChange={(e) => setSendForm({ ...sendForm, content: e.target.value.slice(0, 4096) })}
                helperText={`${sendForm.content.length} / 4096 caracteres`}
                disabled={sendLoading || !number?.has_access_token}
              />
            </Grid>
            <Grid item xs={12}>
              <Stack direction="row" spacing={2} alignItems="center">
                <Button
                  variant="contained"
                  onClick={handleSend}
                  disabled={sendLoading || !number?.has_access_token}
                  startIcon={sendLoading ? <CircularProgress size={16} /> : <SendIcon sx={{ fontSize: 18 }} />}
                >
                  {sendLoading ? 'Enviando...' : 'Enviar mensagem'}
                </Button>
                {!number?.has_access_token && (
                  <Typography variant="caption" color="warning.main" sx={{ fontWeight: 500 }}>
                    Salve as credenciais (Access Token) antes de enviar mensagens.
                  </Typography>
                )}
              </Stack>
            </Grid>
          </Grid>
          {lastSendResult && (
            <Box sx={{ mt: 2.5 }}>
              <Divider sx={{ mb: 2 }} />
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, mb: 1, display: 'block' }}>
                Resultado do último envio
              </Typography>
              {lastSendResult.success ? (
                <Alert
                  severity="success"
                  action={
                    lastSendResult.conversation_id ? (
                      <Button
                        color="inherit"
                        size="small"
                        startIcon={<OpenInNewIcon sx={{ fontSize: 14 }} />}
                        onClick={() => navigate(`/admin/whatsapp-inbox?conversationId=${lastSendResult.conversation_id}`)}
                      >
                        Abrir conversa
                      </Button>
                    ) : undefined
                  }
                >
                  Mensagem enviada com sucesso via <strong>{lastSendResult.provider_used || 'provider'}</strong>
                  {lastSendResult.normalized_to && <> para <strong>{lastSendResult.normalized_to}</strong></>}.
                </Alert>
              ) : lastSendResult.paid_sending_was_blocked ? (
                <Alert severity="warning">
                  <strong>Envio pago bloqueado</strong>: flag <code>WHATSAPP_PAID_SENDING_ENABLED=false</code> no backend.
                  Habilite esta variável no arquivo <code>.env</code> do servidor para liberar disparos pagos pela Cloud API.
                </Alert>
              ) : (
                <Alert severity="error">
                  Erro ao enviar: {lastSendResult.error || lastSendResult.detail || 'sem detalhe'}
                </Alert>
              )}
            </Box>
          )}
        </CardContent>
      </Card>

      {/* AW-011: Inbox + Últimas 5 conversas */}
      <Card variant="outlined">
        <CardContent sx={{ p: 3 }}>
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 2,
              alignItems: 'center',
              justifyContent: 'space-between',
              mb: 2,
            }}
          >
            <Stack direction="row" spacing={1.5} alignItems="center">
              <InboxIcon color="primary" sx={{ fontSize: 24 }} />
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
                  Inbox deste número
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {conversationsLoading ? 'Carregando conversas...' : 'Últimas 5 conversas atualizadas'}
                </Typography>
              </Box>
            </Stack>
            <Stack direction="row" spacing={1.5}>
              <Button
                variant="outlined"
                size="small"
                startIcon={<RefreshIcon sx={{ fontSize: 14 }} />}
                onClick={() => loadRecentConversations()}
                disabled={!number?.id || conversationsLoading}
              >
                Atualizar
              </Button>
              <Button
                variant="contained"
                size="small"
                startIcon={<OpenInNewIcon sx={{ fontSize: 14 }} />}
                onClick={() => navigate(`/admin/whatsapp-inbox${number?.id ? `?numberId=${number.id}` : ''}`)}
                disabled={!number?.id}
              >
                Abrir inbox completo
              </Button>
            </Stack>
          </Box>
          {!number?.id ? (
            <Alert severity="info">Salve as credenciais para visualizar a inbox deste número.</Alert>
          ) : conversationsLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress size={24} />
            </Box>
          ) : recentConversations.length === 0 ? (
            <Alert severity="info">
              Nenhuma conversa ainda. Envie uma mensagem avulsa acima para criar a primeira!
            </Alert>
          ) : (
            <Stack spacing={1} divider={<Divider flexItem />}>
              {recentConversations.map((conv) => (
                <Box
                  key={conv.id}
                  onClick={() => navigate(`/admin/whatsapp-inbox?conversationId=${conv.id}`)}
                  sx={{
                    display: 'flex',
                    gap: 1.5,
                    alignItems: 'center',
                    py: 1,
                    px: 1,
                    borderRadius: 2,
                    cursor: 'pointer',
                    transition: 'background 150ms ease',
                    '&:hover': { bgcolor: (t) => alpha(t.palette.action.hover, 1) },
                  }}
                >
                  <Avatar sx={{ width: 36, height: 36, fontSize: 14, bgcolor: (t) => alpha(t.palette.primary.main, 0.1), color: 'primary.main' }}>
                    {conv.remote_name ? conv.remote_name.slice(0, 2).toUpperCase() : '#'}
                  </Avatar>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Typography variant="body2" sx={{ fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', flex: 1 }}>
                        {conv.remote_name || `Conversa #${conv.id}`}
                      </Typography>
                      {conv.unread_count > 0 && (
                        <Chip
                          label={conv.unread_count}
                          size="small"
                          color="error"
                          sx={{ height: 18, fontSize: 10, borderRadius: 999, '& .MuiChip-label': { px: 1, py: 0 } }}
                        />
                      )}
                      <Typography variant="caption" color="text.secondary">
                        {formatRelative(conv.last_message_at).label}
                      </Typography>
                    </Stack>
                    <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
                      {conv.remote_phone} · Status: {conv.status}
                    </Typography>
                  </Box>
                  <OpenInNewIcon sx={{ fontSize: 16, color: 'text.disabled' }} />
                </Box>
              ))}
            </Stack>
          )}
        </CardContent>
      </Card>

      {/* AW-012: Tabela observabilidade eventos inbound */}
      <Card variant="outlined">
        <CardContent sx={{ p: 3 }}>
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 2,
              alignItems: 'center',
              justifyContent: 'space-between',
              mb: 2,
            }}
          >
            <Stack direction="row" spacing={1.5} alignItems="center">
              <DescriptionIcon color="primary" sx={{ fontSize: 24 }} />
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
                  Últimos eventos de entrada (diagnóstico)
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {inboundPagination.total} evento(s) · Página {inboundPagination.page} / {totalPages}
                </Typography>
              </Box>
            </Stack>
            <Stack direction="row" spacing={1.5}>
              <Button
                variant="outlined"
                size="small"
                startIcon={<FilterListIcon sx={{ fontSize: 14 }} />}
                onClick={() => setInboundFiltersOpen((v) => !v)}
              >
                Filtros
              </Button>
              <Button
                variant="contained"
                size="small"
                startIcon={<RefreshIcon sx={{ fontSize: 14 }} />}
                onClick={() => loadInboundList()}
                disabled={!number?.id || inboundLoading}
              >
                Recarregar
              </Button>
            </Stack>
          </Box>

          {inboundFiltersOpen && (
            <Box sx={{ mb: 2, p: 2, bgcolor: (t) => alpha(t.palette.grey[100], 0.5), borderRadius: 2, border: 1, borderColor: (t) => t.palette.divider }}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={3}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Fonte</InputLabel>
                    <Select
                      value={inboundFilters.source}
                      label="Fonte"
                      onChange={(e) => { setInboundFilters((f) => ({ ...f, source: e.target.value as any })); setInboundPagination((p) => ({ ...p, page: 1 })); }}
                    >
                      <MenuItem value="">Todas</MenuItem>
                      <MenuItem value="meta_webhook">Meta Webhook</MenuItem>
                      <MenuItem value="baileys_bridge">Bridge Baileys</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Telefone remoto (parcial)"
                    value={inboundFilters.remote_phone}
                    onChange={(e) => { setInboundFilters((f) => ({ ...f, remote_phone: e.target.value })); setInboundPagination((p) => ({ ...p, page: 1 })); }}
                    placeholder="Ex: 96511"
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    fullWidth
                    size="small"
                    type="datetime-local"
                    label="De"
                    value={inboundFilters.date_from}
                    InputLabelProps={{ shrink: true }}
                    onChange={(e) => { setInboundFilters((f) => ({ ...f, date_from: e.target.value })); setInboundPagination((p) => ({ ...p, page: 1 })); }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    fullWidth
                    size="small"
                    type="datetime-local"
                    label="Até"
                    value={inboundFilters.date_to}
                    InputLabelProps={{ shrink: true }}
                    onChange={(e) => { setInboundFilters((f) => ({ ...f, date_to: e.target.value })); setInboundPagination((p) => ({ ...p, page: 1 })); }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Stack direction="row" spacing={1} flexWrap="wrap">
                    {(Object.keys(PRETTY_EVENT_RESULT) as WhatsAppInboundEventResult[]).map((r) => {
                      const active = inboundFilters.event_result.includes(r);
                      const isError = r !== 'ingested';
                      return (
                        <Chip
                          key={r}
                          size="small"
                          label={PRETTY_EVENT_RESULT[r]}
                          onClick={() => toggleResultFilter(r)}
                          onDelete={active ? () => toggleResultFilter(r) : undefined}
                          color={active ? (isError ? 'error' : 'success') : 'default'}
                          variant={active ? 'filled' : 'outlined'}
                          sx={{ borderRadius: 999, mb: 0.5 }}
                        />
                      );
                    })}
                  </Stack>
                </Grid>
              </Grid>
            </Box>
          )}

          {!number?.id ? (
            <Alert severity="info">Salve o número para visualizar eventos de entrada.</Alert>
          ) : inboundLoading && inboundList.length === 0 ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>
          ) : inboundList.length === 0 ? (
            <Alert severity="info">
              Nenhum evento de entrada encontrado. Envie uma mensagem de teste de outro celular para este número para gerar o primeiro evento.
            </Alert>
          ) : (
            <>
              <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: (t) => alpha(t.palette.grey[200], 0.4) }}>
                      <TableCell sx={{ fontWeight: 600, fontSize: 12 }}>Quando</TableCell>
                      <TableCell sx={{ fontWeight: 600, fontSize: 12 }}>Resultado</TableCell>
                      <TableCell sx={{ fontWeight: 600, fontSize: 12 }}>Fonte</TableCell>
                      <TableCell sx={{ fontWeight: 600, fontSize: 12 }}>Remetente</TableCell>
                      <TableCell sx={{ fontWeight: 600, fontSize: 12 }}>Phone ID (meta)</TableCell>
                      <TableCell sx={{ fontWeight: 600, fontSize: 12 }} align="right">Ação</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {inboundList.map((ev) => {
                      const ok = ev.event_result === 'ingested';
                      return (
                        <TableRow key={ev.id} hover sx={{ '&:last-child td': { borderBottom: 0 } }}>
                          <TableCell sx={{ verticalAlign: 'middle' }}>
                            <Box title={formatRelative(ev.created_at).title}>
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {formatRelative(ev.created_at).label}
                              </Typography>
                              <Typography variant="caption" color="text.secondary" component="div">
                                {new Date(ev.created_at).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell sx={{ verticalAlign: 'middle' }}>
                            <Chip
                              size="small"
                              label={PRETTY_EVENT_RESULT[ev.event_result]}
                              color={ok ? 'success' : (ev.event_result === 'discarded_duplicate_external_message_id' ? 'default' : 'error')}
                              variant="filled"
                              sx={{ borderRadius: 999, fontSize: 11, height: 20, fontWeight: 600 }}
                            />
                          </TableCell>
                          <TableCell sx={{ verticalAlign: 'middle' }}>
                            <Chip
                              size="small"
                              label={ev.source === 'meta_webhook' ? 'Meta' : 'Baileys'}
                              variant="outlined"
                              sx={{ borderRadius: 999, fontSize: 10, height: 18 }}
                            />
                          </TableCell>
                          <TableCell sx={{ verticalAlign: 'middle' }}>
                            <Typography variant="body2" sx={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: 12 }}>
                              {ev.remote_phone_normalized || '—'}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ verticalAlign: 'middle' }}>
                            <Typography variant="caption" sx={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', color: 'text.secondary' }}>
                              {ev.metadata_phone_number_id || '—'}
                            </Typography>
                          </TableCell>
                          <TableCell align="right" sx={{ verticalAlign: 'middle' }}>
                            <Button size="small" variant="text" onClick={() => openEventDetail(ev.id)} startIcon={<DescriptionIcon sx={{ fontSize: 14 }} />}>
                              Detalhe
                            </Button>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
              <Stack direction="row" spacing={1} justifyContent="flex-end" alignItems="center" sx={{ mt: 2 }}>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => setInboundPagination((p) => ({ ...p, page: Math.max(1, p.page - 1) }))}
                  disabled={inboundPagination.page <= 1 || inboundLoading}
                >
                  Anterior
                </Button>
                <FormControl size="small" sx={{ minWidth: 80 }}>
                  <Select
                    value={inboundPagination.per_page}
                    onChange={(e) => setInboundPagination((p) => ({ ...p, per_page: Number(e.target.value), page: 1 }))}
                    disabled={inboundLoading}
                  >
                    <MenuItem value={5}>5 / pág</MenuItem>
                    <MenuItem value={10}>10 / pág</MenuItem>
                    <MenuItem value={20}>20 / pág</MenuItem>
                    <MenuItem value={50}>50 / pág</MenuItem>
                  </Select>
                </FormControl>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => setInboundPagination((p) => ({ ...p, page: Math.min(totalPages, p.page + 1) }))}
                  disabled={inboundPagination.page >= totalPages || inboundLoading}
                >
                  Próxima
                </Button>
              </Stack>
            </>
          )}
        </CardContent>
      </Card>

      {/* Modal detail evento inbound (AW-012) */}
      <Dialog open={detailOpen} onClose={() => setDetailOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', gap: 1.5, alignItems: 'center', pb: 1 }}>
          <DescriptionIcon color="primary" />
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
              Detalhe do evento #{selectedEventDetail?.id ?? ''}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {selectedEventDetail?.created_at
                ? new Date(selectedEventDetail.created_at).toLocaleString('pt-BR')
                : ''}
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {detailLoading && !selectedEventDetail ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>
          ) : selectedEventDetail ? (
            <Stack spacing={2.5}>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>Resultado</Typography>
                  <Box sx={{ mt: 0.5 }}>
                    <Chip
                      size="small"
                      label={PRETTY_EVENT_RESULT[selectedEventDetail.event_result]}
                      color={selectedEventDetail.event_result === 'ingested' ? 'success' : 'error'}
                      variant="filled"
                      sx={{ borderRadius: 999, fontWeight: 600, height: 22 }}
                    />
                  </Box>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>Fonte</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600, mt: 0.5 }}>
                    {selectedEventDetail.source === 'meta_webhook' ? 'Meta Webhook' : 'Bridge Baileys'}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>Telefone remetente</Typography>
                  <Typography variant="body2" sx={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', mt: 0.5 }}>
                    {selectedEventDetail.remote_phone_normalized || '—'}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>ID mensagem (externo)</Typography>
                  <Typography variant="body2" sx={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', mt: 0.5 }} noWrap>
                    {selectedEventDetail.external_message_id || '—'}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>Phone Number ID (meta)</Typography>
                  <Typography variant="body2" sx={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', mt: 0.5 }} noWrap>
                    {selectedEventDetail.metadata_phone_number_id || '—'}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>Org · Número ID</Typography>
                  <Typography variant="body2" sx={{ mt: 0.5 }}>
                    Org #{selectedEventDetail.organization_id} · Número #{selectedEventDetail.number_id ?? '—'}
                  </Typography>
                </Grid>
              </Grid>
              <Divider />
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500, mb: 0.5, display: 'block' }}>
                  Detalhe do erro
                </Typography>
                {selectedEventDetail.error_detail ? (
                  <Paper variant="outlined" sx={{ p: 1.5, bgcolor: (t) => alpha(t.palette.error.main, 0.04), borderRadius: 1.5, borderColor: (t) => alpha(t.palette.error.main, 0.3) }}>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', color: 'error.main', fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: 12, lineHeight: 1.5 }}>
                      {selectedEventDetail.error_detail}
                    </Typography>
                  </Paper>
                ) : (
                  <Typography variant="caption" color="text.secondary">Sem erro registrado.</Typography>
                )}
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500, mb: 0.5, display: 'block' }}>
                  Preview do payload (truncado em 2000 chars)
                </Typography>
                {selectedEventDetail.payload_preview ? (
                  <Paper variant="outlined" sx={{ p: 1.5, bgcolor: (t) => alpha(t.palette.grey[100], 0.5), borderRadius: 1.5, maxHeight: 260, overflow: 'auto' }}>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: 12, lineHeight: 1.5 }}>
                      {selectedEventDetail.payload_preview}
                    </Typography>
                  </Paper>
                ) : (
                  <Typography variant="caption" color="text.secondary">Sem payload registrado.</Typography>
                )}
              </Box>
            </Stack>
          ) : null}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setDetailOpen(false)} variant="outlined">Fechar</Button>
        </DialogActions>
      </Dialog>

      {/* Formulário de credenciais */}
      <Card variant="outlined">
        <CardContent sx={{ p: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
            {number ? 'Editar Credenciais Cloud API' : 'Configurar Número Cloud API'}
          </Typography>
          <Alert severity="info" sx={{ mb: 3 }}>
            Credenciais da WhatsApp Cloud API (Meta) desta organização. Gere o Phone Number ID e o
            Access Token no Business Manager (Configurações do Negócio → Usuários do Sistema) e cole
            aqui. Todo o disparo de mensagens desta organização passa a usar essas credenciais.
          </Alert>
          <Box sx={{ maxWidth: 600 }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Rótulo"
                  value={form.label}
                  onChange={(e) => setForm({ ...form, label: e.target.value })}
                  required
                  helperText="Nome interno para identificar este número"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Phone Number ID"
                  value={form.phone_number_id}
                  onChange={(e) => setForm({ ...form, phone_number_id: e.target.value })}
                  required
                  helperText="ID numérico do número, obtido no Meta Developer Portal"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Access Token"
                  type={showToken ? 'text' : 'password'}
                  value={form.access_token}
                  onChange={(e) => setForm({ ...form, access_token: e.target.value })}
                  required={!number}
                  placeholder={number?.has_access_token ? `Configurado, terminando em ${number.access_token_preview}` : ''}
                  helperText={
                    number?.has_access_token
                      ? 'Deixe em branco para manter o token atual'
                      : 'Token de acesso permanente gerado no Business Manager'
                  }
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={() => setShowToken(!showToken)} edge="end">
                          {showToken ? <VisibilityOffIcon /> : <VisibilityIcon />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Business Account ID"
                  value={form.business_account_id}
                  onChange={(e) => setForm({ ...form, business_account_id: e.target.value })}
                  helperText="ID da WhatsApp Business Account (WABA) conectada"
                />
              </Grid>
              <Grid item xs={12}>
                <Button
                  variant="contained"
                  onClick={handleSave}
                  disabled={saving}
                  startIcon={saving ? <CircularProgress size={16} /> : undefined}
                >
                  {number ? 'Salvar alterações' : 'Salvar configuração'}
                </Button>
              </Grid>
            </Grid>
          </Box>
        </CardContent>
      </Card>
    </Stack>
  );
};

const ChatbotPromptConfigs: React.FC = () => {
  const { user, isAuthenticated } = useAuth();
  const [tabValue, setTabValue] = useState(0);
  const [configs, setConfigs] = useState<ChatbotPromptConfig[]>([]);
  const [loading, setLoading] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [editingConfig, setEditingConfig] = useState<ChatbotPromptConfig | null>(null);
  const [formData, setFormData] = useState<ChatbotPromptConfigFormData>({
    business_type: 'consórcio',
    main_prompt: '',
    personality: 'persuasivo e amigável',
    objectives: '',
    benefits: '',
    questions: '',
    closing_phrases: '',
    tone: 'humano e consultivo',
    is_active: true,
    voice_enabled: false,
    voice_type: 'alloy',
    voice_speed: 1.0,
    voice_model: 'tts-1',
    auto_voice_response: false,
  });
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error' | 'info' | 'warning',
  });
  const [deleteDialog, setDeleteDialog] = useState({
    open: false,
    configId: 0,
    configName: '',
  });
  const navigate = useNavigate();

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    if (user?.role !== 'admin') {
      navigate('/dashboard');
      return;
    }
  }, [isAuthenticated, user, navigate]);

  const showSnackbar = useCallback((message: string, severity: 'success' | 'error' | 'info' | 'warning') => {
    setSnackbar({ open: true, message, severity });
  }, []);

  const fetchConfigs = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/chatbot/prompt-configs/`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setConfigs(response.data);
    } catch (error) {
      showSnackbar('Erro ao carregar configurações', 'error');
    }
    setLoading(false);
  }, [showSnackbar]);

  useEffect(() => {
    fetchConfigs();
  }, [fetchConfigs]);

  const handleOpenModal = (configToEdit?: ChatbotPromptConfig) => {
    if (configToEdit) {
      setEditingConfig(configToEdit);
      setFormData({
        business_type: configToEdit.business_type,
        main_prompt: configToEdit.main_prompt,
        personality: configToEdit.personality,
        objectives: configToEdit.objectives,
        benefits: configToEdit.benefits || '',
        questions: configToEdit.questions || '',
        closing_phrases: configToEdit.closing_phrases || '',
        tone: configToEdit.tone,
        is_active: configToEdit.is_active,
        voice_enabled: configToEdit.voice_enabled || false,
        voice_type: configToEdit.voice_type || 'alloy',
        voice_speed: configToEdit.voice_speed || 1.0,
        voice_model: configToEdit.voice_model || 'tts-1',
        auto_voice_response: configToEdit.auto_voice_response || false,
      });
    } else {
      setEditingConfig(null);
      setFormData({
        business_type: 'consórcio',
        main_prompt: '',
        personality: 'persuasivo e amigável',
        objectives: '',
        benefits: '',
        questions: '',
        closing_phrases: '',
        tone: 'humano e consultivo',
        is_active: true,
        voice_enabled: false,
        voice_type: 'alloy',
        voice_speed: 1.0,
        voice_model: 'tts-1',
        auto_voice_response: false,
      });
    }
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setEditingConfig(null);
    setFormData({
      business_type: 'consórcio',
      main_prompt: '',
      personality: 'persuasivo e amigável',
      objectives: '',
      benefits: '',
      questions: '',
      closing_phrases: '',
      tone: 'humano e consultivo',
      is_active: true,
      voice_enabled: false,
      voice_type: 'alloy',
      voice_speed: 1.0,
      voice_model: 'tts-1',
      auto_voice_response: false,
    });
  };

  const validateForm = () => {
    const errors: string[] = [];
    
    if (!formData.main_prompt.trim()) {
      errors.push('Prompt principal é obrigatório');
    }
    
    if (!formData.objectives.trim()) {
      errors.push('Objetivos são obrigatórios');
    }
    
    return errors;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const validationErrors = validateForm();
    if (validationErrors.length > 0) {
      showSnackbar(validationErrors.join(', '), 'error');
      return;
    }
    
    setLoading(true);

    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      if (editingConfig) {
        // Atualizar configuração existente
        await axios.put(`${API_URL}/chatbot/prompt-configs/${editingConfig.id}`, formData, { headers });
        showSnackbar('Configuração atualizada com sucesso!', 'success');
      } else {
        // Criar nova configuração
        await axios.post(`${API_URL}/chatbot/prompt-configs/`, formData, { headers });
        showSnackbar('Configuração criada com sucesso!', 'success');
      }

      handleCloseModal();
      fetchConfigs();
    } catch (error: any) {
      const message = error.response?.data?.detail || 'Erro ao salvar configuração';
      showSnackbar(message, 'error');
    }
    setLoading(false);
  };

  const handleDelete = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/chatbot/prompt-configs/${deleteDialog.configId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      showSnackbar('Configuração desativada com sucesso!', 'success');
      setDeleteDialog({ open: false, configId: 0, configName: '' });
      fetchConfigs();
    } catch (error: any) {
      const message = error.response?.data?.detail || 'Erro ao desativar configuração';
      showSnackbar(message, 'error');
    }
    setLoading(false);
  };





  const getPersonalityColor = (personality: string) => {
    switch (personality.toLowerCase()) {
      case 'persuasivo e amigável':
        return 'primary';
      case 'formal e profissional':
        return 'secondary';
      case 'casual e descontraído':
        return 'success';
      default:
        return 'default';
    }
  };

  if (loading && configs.length === 0) {
    return (
      <Container maxWidth="lg">
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Configurações de Prompt do Chatbot
        </Typography>
        <Typography variant="subtitle1" color="text.secondary">
          Gerencie as configurações de prompt e a integração de WhatsApp do chatbot por cliente.
        </Typography>
      </Box>

      <Paper sx={{ mb: 3 }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={tabValue} onChange={(_e, newValue) => setTabValue(newValue)}>
            <Tab icon={<SettingsIcon />} iconPosition="start" label="Prompts do Chatbot" />
            <Tab icon={<WhatsAppIcon />} iconPosition="start" label="WhatsApp" />
          </Tabs>
        </Box>
      </Paper>

      <CustomTabPanel value={tabValue} index={0}>
      {/* Cards de Estatísticas */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <SettingsIcon color="primary" sx={{ mr: 1 }} />
                <Typography variant="h6">Total de Configs</Typography>
              </Box>
              <Typography variant="h4" color="primary">
                {configs.length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <BotIcon color="success" sx={{ mr: 1 }} />
                <Typography variant="h6">Ativas</Typography>
              </Box>
              <Typography variant="h4" color="success.main">
                {configs.filter(c => c.is_active).length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <BusinessIcon color="warning" sx={{ mr: 1 }} />
                <Typography variant="h6">Tipos de Negócio</Typography>
              </Box>
              <Typography variant="h4" color="warning.main">
                {new Set(configs.map(c => c.business_type)).size}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <PsychologyIcon color="secondary" sx={{ mr: 1 }} />
                <Typography variant="h6">Personalidades</Typography>
              </Box>
              <Typography variant="h4" color="secondary.main">
                {new Set(configs.map(c => c.personality)).size}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Tabela de Configurações */}
      <Paper sx={{ width: '100%', overflow: 'hidden' }}>
        <TableContainer sx={{ maxHeight: 600 }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>

                <TableCell>Tipo de Negócio</TableCell>
                <TableCell>Personalidade</TableCell>
                <TableCell>Tom</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Criado em</TableCell>
                <TableCell align="center">Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {configs.map((config) => (
                <TableRow key={config.id} hover>

                  <TableCell>
                    <Chip
                      label={config.business_type}
                      size="small"
                      color="primary"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={config.personality}
                      size="small"
                      color={getPersonalityColor(config.personality) as any}
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {config.tone}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={config.is_active ? 'Ativa' : 'Inativa'}
                      size="small"
                      color={config.is_active ? 'success' : 'default'}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {new Date(config.created_at).toLocaleDateString('pt-BR')}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title="Editar">
                      <IconButton
                        size="small"
                        onClick={() => handleOpenModal(config)}
                        color="primary"
                      >
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Desativar">
                      <IconButton
                        size="small"
                        onClick={() => setDeleteDialog({
                          open: true,
                          configId: config.id,
                          configName: config.business_type
                        })}
                        color="error"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Botão de Adicionar */}
      <Tooltip title="Adicionar Nova Configuração">
        <Fab
          color="primary"
          aria-label="add"
          sx={{ position: 'fixed', bottom: 16, right: 16 }}
          onClick={() => handleOpenModal()}
        >
          <AddIcon />
        </Fab>
      </Tooltip>
      </CustomTabPanel>

      <CustomTabPanel value={tabValue} index={1}>
        <WhatsAppCloudApiTab showSnackbar={showSnackbar} />
      </CustomTabPanel>

      {/* Modal de Criação/Edição */}
      <Dialog open={openModal} onClose={handleCloseModal} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <SettingsIcon sx={{ mr: 1 }} />
            {editingConfig ? 'Editar Configuração' : 'Nova Configuração'}
          </Box>
        </DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent>
            <Grid container spacing={2}>

              <Grid item xs={12} sm={6}>
                <FormControl fullWidth required>
                  <InputLabel>Tipo de Negócio</InputLabel>
                  <Select
                    value={formData.business_type}
                    onChange={(e) => setFormData({ ...formData, business_type: e.target.value })}
                    label="Tipo de Negócio"
                  >
                    <MenuItem value="consórcio">Consórcio</MenuItem>
                    <MenuItem value="imóveis">Imóveis</MenuItem>
                    <MenuItem value="veículos">Veículos</MenuItem>
                    <MenuItem value="financeiro">Financeiro</MenuItem>
                    <MenuItem value="seguros">Seguros</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth required>
                  <InputLabel>Personalidade</InputLabel>
                  <Select
                    value={formData.personality}
                    onChange={(e) => setFormData({ ...formData, personality: e.target.value })}
                    label="Personalidade"
                  >
                    <MenuItem value="persuasivo e amigável">Persuasivo e Amigável</MenuItem>
                    <MenuItem value="formal e profissional">Formal e Profissional</MenuItem>
                    <MenuItem value="casual e descontraído">Casual e Descontraído</MenuItem>
                    <MenuItem value="consultivo e especialista">Consultivo e Especialista</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Prompt Principal"
                  value={formData.main_prompt}
                  onChange={(e) => setFormData({ ...formData, main_prompt: e.target.value })}
                  required
                  multiline
                  rows={4}
                  helperText="Prompt principal que define o comportamento do chatbot"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Objetivos"
                  value={formData.objectives}
                  onChange={(e) => setFormData({ ...formData, objectives: e.target.value })}
                  required
                  multiline
                  rows={3}
                  helperText="Objetivos principais do chatbot para este cliente"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Benefícios"
                  value={formData.benefits}
                  onChange={(e) => setFormData({ ...formData, benefits: e.target.value })}
                  multiline
                  rows={3}
                  helperText="Benefícios a serem destacados"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Perguntas Sugeridas"
                  value={formData.questions}
                  onChange={(e) => setFormData({ ...formData, questions: e.target.value })}
                  multiline
                  rows={3}
                  helperText="Perguntas que o chatbot pode fazer"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Frases de Fechamento"
                  value={formData.closing_phrases}
                  onChange={(e) => setFormData({ ...formData, closing_phrases: e.target.value })}
                  multiline
                  rows={2}
                  helperText="Frases para finalizar conversas"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth required>
                  <InputLabel>Tom de Voz</InputLabel>
                  <Select
                    value={formData.tone}
                    onChange={(e) => setFormData({ ...formData, tone: e.target.value })}
                    label="Tom de Voz"
                  >
                    <MenuItem value="humano e consultivo">Humano e Consultivo</MenuItem>
                    <MenuItem value="profissional e direto">Profissional e Direto</MenuItem>
                    <MenuItem value="amigável e informal">Amigável e Informal</MenuItem>
                    <MenuItem value="técnico e especializado">Técnico e Especializado</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              
              {/* Configurações de Voz */}
              <Grid item xs={12}>
                <Typography variant="h6" sx={{ mt: 2, mb: 1, color: 'primary.main' }}>
                  🎤 Configurações de Voz
                </Typography>
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.voice_enabled}
                      onChange={(e) => setFormData({ ...formData, voice_enabled: e.target.checked })}
                      color="primary"
                    />
                  }
                  label="Habilitar Voz"
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.auto_voice_response}
                      onChange={(e) => setFormData({ ...formData, auto_voice_response: e.target.checked })}
                      color="primary"
                      disabled={!formData.voice_enabled}
                    />
                  }
                  label="Resposta Automática em Voz"
                />
              </Grid>
              
              {formData.voice_enabled && (
                <>
                  <Grid item xs={12} sm={4}>
                    <FormControl fullWidth>
                      <InputLabel>Tipo de Voz</InputLabel>
                      <Select
                        value={formData.voice_type}
                        onChange={(e) => setFormData({ ...formData, voice_type: e.target.value })}
                        label="Tipo de Voz"
                      >
                        <MenuItem value="alloy">Alloy (Neutro)</MenuItem>
                        <MenuItem value="echo">Echo (Masculino)</MenuItem>
                        <MenuItem value="fable">Fable (Britânico)</MenuItem>
                        <MenuItem value="onyx">Onyx (Profundo)</MenuItem>
                        <MenuItem value="nova">Nova (Feminino)</MenuItem>
                        <MenuItem value="shimmer">Shimmer (Feminino Suave)</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  
                  <Grid item xs={12} sm={4}>
                    <FormControl fullWidth>
                      <InputLabel>Velocidade da Voz</InputLabel>
                      <Select
                        value={formData.voice_speed}
                        onChange={(e) => setFormData({ ...formData, voice_speed: parseFloat(String(e.target.value)) })}
                        label="Velocidade da Voz"
                      >
                        <MenuItem value={0.25}>0.25x (Muito Lenta)</MenuItem>
                        <MenuItem value={0.5}>0.5x (Lenta)</MenuItem>
                        <MenuItem value={0.75}>0.75x (Pouco Lenta)</MenuItem>
                        <MenuItem value={1.0}>1.0x (Normal)</MenuItem>
                        <MenuItem value={1.25}>1.25x (Pouco Rápida)</MenuItem>
                        <MenuItem value={1.5}>1.5x (Rápida)</MenuItem>
                        <MenuItem value={2.0}>2.0x (Muito Rápida)</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  
                  <Grid item xs={12} sm={4}>
                    <FormControl fullWidth>
                      <InputLabel>Modelo de Voz</InputLabel>
                      <Select
                        value={formData.voice_model}
                        onChange={(e) => setFormData({ ...formData, voice_model: e.target.value })}
                        label="Modelo de Voz"
                      >
                        <MenuItem value="tts-1">TTS-1 (Rápido)</MenuItem>
                        <MenuItem value="tts-1-hd">TTS-1-HD (Alta Qualidade)</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                </>
              )}
              
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.is_active}
                      onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                      color="primary"
                    />
                  }
                  label="Configuração Ativa"
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseModal}>Cancelar</Button>
            <Button type="submit" variant="contained" disabled={loading}>
              {loading ? <CircularProgress size={20} /> : (editingConfig ? 'Atualizar' : 'Criar')}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Dialog de Confirmação de Exclusão */}
      <Dialog open={deleteDialog.open} onClose={() => setDeleteDialog({ open: false, configId: 0, configName: '' })}>
        <DialogTitle>Confirmar Desativação</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Tem certeza que deseja desativar a configuração "{deleteDialog.configName}"?
            Esta ação pode ser revertida editando a configuração posteriormente.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog({ open: false, configId: 0, configName: '' })}>Cancelar</Button>
          <Button onClick={handleDelete} color="error" disabled={loading}>
            {loading ? <CircularProgress size={20} /> : 'Desativar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default ChatbotPromptConfigs;
